<template>
  <div class="explore-filters">
    <div class="d-flex align-center justify-space-between flex-wrap mt-n2">
      <div class="d-flex align-center flex-wrap">
        <UserFilters
          ref="UserFilters"
          @onSelectFilter="handleSelectFilter($event)"
          @openDrawer="toggleUserFiltersDrawer"
        />
        <UserFiltersChips
          ref="UserFiltersChips"
          @onRemoveActiveFilter="resetRoute"
          @onSelectFilter="handleSelectFilter($event)"
        />
        <UserFiltersDrawer
          v-if="isUserFiltersDrawerOpen"
          :visible="isUserFiltersDrawerOpen"
          @close="toggleUserFiltersDrawer"
          @onRemoveActiveFilter="resetRoute"
          @onChange="refreshUserFilters"
        />
        <UserFiltersSave
          v-if="isUserFilterSavingOpen"
          :isLoading="isSaving"
          @save="handleSaveFilter"
          @close="isUserFilterSavingOpen = false"
        />
      </div>
      <div class="mt-2">
        <Button
          class="mx-0 px-3 py-2 me-2"
          plain
          size="small"
          :text="$t('filters.reset_filters')"
          @click="resetRoute"
        />

        <v-btn
          outlined
          color="blue-grey lighten-2"
          class="mx-0 px-2 py-2 ml-1"
          :class="{ 'active-button': showFilters }"
          @click="toggleFilters"
        >
          <v-icon color="blue-grey darken-1" left>mdi-filter-variant</v-icon>
          <span class="blue-grey--text text--darken-3 caption">
            {{ $t('filters.filter_other') }}
            <span v-if="selectedFiltersCount">
              ({{ selectedFiltersCount }})
            </span>
          </span>
        </v-btn>
      </div>
    </div>
    <div class="filters-container" :class="{ 'hide-filters': !showFilters }">
      <Filters
        class="mt-6"
        :disableApplyButton="isApplyDisabled"
        :initialFilters="initialFilters"
        :hideSaveButton="shouldHideSaveButton"
        @apply="handleApplyFilters"
        @change="handleFilterChange"
        @save="handleOpenSaveDialog"
        @initialFiltersLoad="handleInitialFiltersLoad"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'

import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import Button from '@/components/UI/Button.vue'
import Filters from '@/components/Filters/Filters.vue'
import UserFilters from '@/containers/Explore/Filters/UserFilters.vue'
import UserFiltersChips from '@/containers/Explore/Filters/UserFiltersChips.vue'
import UserFiltersDrawer from '@/containers/Explore/Filters/UserFiltersDrawer.vue'
import UserFiltersSave from '@/containers/Explore/Filters/UserFiltersSave.vue'

export default {
  name: 'ExploreFiltersNew',

  mixins: [mixin, trackingMixin],

  components: {
    Button,
    Filters,
    UserFilters,
    UserFiltersChips,
    UserFiltersDrawer,
    UserFiltersSave,
  },
  data() {
    return {
      isUserFiltersDrawerOpen: false,
      isUserFilterSavingOpen: false,
      isSaving: false,
      showFilters: true,
      initialFilters: {
        page: 1,
        types: [],
        status: [],
      },
      currentFilters: {},
      currentFiltersApplied: {},
    }
  },

  created() {
    const queryFilters = _.omit(this.$route.query, [
      'sitesOption',
      'typesOption',
      'roleOption',
      'statusOption',
    ])

    const normalizedSites = this.normalizeSitesFromQuery(this.$route.query)
    const hasExplicitSitesInQuery = normalizedSites !== undefined

    if (hasExplicitSitesInQuery) {
      queryFilters.sites = normalizedSites
    }

    if (this.$route.query?.statusOption) {
      queryFilters.status = []
    }

    if (this.$route.query?.typesOption) {
      queryFilters.types = []
    }

    if (
      this.$route.query?.roleOption ||
      (!this.$route.query?.collaborators &&
        !this.$route.query?.owner &&
        !this.$route.query?.coach)
    ) {
      queryFilters.role = ''
    }

    const shouldAddSiteFilter =
      !hasExplicitSitesInQuery &&
      Object.values(queryFilters).every(_.isEmpty)

    this.initialFilters = shouldAddSiteFilter
      ? {
          sites: [this.account.site._id], // Account site should be added by default if no other filters are present
          ...this.initialFilters,
          ...queryFilters,
        }
      : {
          ...this.initialFilters,
          ...queryFilters,
        }
  },

  computed: {
    ...mapGetters(['account', 'organization']),

    shouldHideSaveButton() {
      return Boolean(this.$route.query?.userFilter)
    },

    selectedFiltersCount() {
      return this.currentFilters?.deserializedFilters?.filter(
        filter => !_.isEmpty(filter.value)
      ).length
    },

    isApplyDisabled() {
      return _.isEqual(
        this.currentFiltersApplied,
        this.currentFilters?.serializedFilters
      )
    },
  },

  methods: {
    normalizeSitesFromQuery(query) {
      const sitesFromQuery = query?.sites

      if (sitesFromQuery === undefined) return undefined

      if (_.isArray(sitesFromQuery)) {
        return sitesFromQuery.filter(Boolean)
      }

      if (_.isEmpty(sitesFromQuery)) return []

      return [sitesFromQuery]
    },

    handleInitialFiltersLoad(currentFilters) {
      this.currentFilters = currentFilters
      this.currentFiltersApplied = this.currentFilters.serializedFilters
      this.updateRoute({
        ...this.currentFiltersApplied,
        userFilter: this.initialFilters.userFilter,
        userFilterName: this.initialFilters.userFilterName,
      })
      this.$emit('change', this.currentFiltersApplied)
    },

    handleSelectFilter(newFilter) {
      this.initialFilters = _.omitBy(
        newFilter,
        value => (_.isArray(value) && _.isEmpty(value)) || _.isNull(value)
      )
    },

    updateRoute(newFilters) {
      const newFiltersParsed = Object.entries(newFilters).reduce(
        (previousValue, [currentKey, currentValue]) => {
          if (currentValue?.from && currentValue?.to) {
            return (previousValue = {
              ...previousValue,
              [currentKey]: [currentValue.from, currentValue.to],
            })
          }

          return (previousValue = {
            ...previousValue,
            [currentKey]: currentValue,
          })
        },
        {}
      )

      this.$router
        .replace({
          path: 'explore',
          query: {
            ...newFiltersParsed,
            page: 1,
            view: this.$route.query.view,
            sort: this.$route.query.sort,
          },
        })
        .catch(() => {})
    },

    resetRoute() {
      const query = {
        page: 1,
        sites: [this.account.site._id],
        types: [],
        status: [],
        role: null,
        view: this.$route.query.view,
      }
      this.trackFilterActions('explore.resetFilters')
      this.updateRoute(query)
      this.initialFilters = query
    },

    toggleFilters() {
      this.showFilters = !this.showFilters
      this.trackFilterActions(
        this.showFilters ? 'explore.collapseFilters' : 'explore.expandFilters'
      )
    },

    handleFilterChange(filters) {
      this.currentFilters = filters
    },

    async handleSaveFilter(name) {
      try {
        this.isSaving = true
        await api.userFilter.create(this.organization._id, {
          name,
          filters: _.omitBy(
            this.currentFilters.serializedFilters,
            value => (_.isObject(value) && _.isEmpty(value)) || _.isNull(value)
          ),
        })
        this.refreshUserFilters()
        this.isUserFilterSavingOpen = false
        this.showSuccess(this.$t('filters.filter_saved'))
        this.trackFilterActions(
          'my_filters.save',
          this.currentFilters.serializedFilters
        )
      } catch (err) {
        this.showError(err)
      } finally {
        this.isSaving = false
      }
    },

    refreshUserFilters() {
      this.$refs.UserFilters.fetchUserFilters()
      this.$refs.UserFiltersChips.fetchPinnedFilters()
    },

    toggleUserFiltersDrawer() {
      this.isUserFiltersDrawerOpen = !this.isUserFiltersDrawerOpen
    },

    handleApplyFilters() {
      const newQueryParams = {
        ...this.currentFilters.serializedFilters,
        userFilter: undefined,
        userFilterName: undefined,
        page: 1,
      }

      this.updateRoute(newQueryParams)
      this.currentFiltersApplied = _.cloneDeep(
        this.currentFilters.serializedFilters
      )
      this.$emit('change', this.currentFiltersApplied)
    },

    handleOpenSaveDialog() {
      this.isUserFilterSavingOpen = true
      this.trackFilterActions('explore.openSaveFiltersModal')
    },
  },
}
</script>

<style lang="scss" scoped>
.active-button {
  background-color: $color-blue-grey-lighten3;
}

.explore-filters .filters-container {
  transition: max-height 0.5s;
  max-height: 800px;
}

.explore-filters .filters-container.hide-filters {
  transition: max-height 0.5s;
  overflow: hidden;
  max-height: 0;
}

.reset-filters-button:hover {
  opacity: 0.7;
}
</style>
